import {
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Col,
  Form,
  FormGroup,
  Spinner,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { createUser, getAllRoles } from "../../../../store/slices/usersSlice";
import Select from "react-select";
import { useEffect, useMemo, useCallback, memo, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import Swal from "sweetalert2";
import { defaultPasswordGenerate } from "../../../../Utils/defaultPasswordGenerate";
import { updateUser } from "../../../../store/slices/usersSlice";
import { createUserSchema } from "../../../../Utils/validations/users";
import { zodResolver } from "@hookform/resolvers/zod";

/**
 * This component user for Create and Update the User details
 * -if SelectUser details is available then edit or update the user details user
 * -if selectUser details is not there then use for Create User
 * -default this component is CREATE USER
 */

const CreateUserModal = ({ isOpen, toggle, selectedUser, fetchUser }) => {
  const TYPE = selectedUser ? "UPDATE" : "CREATE";
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const rolesData = useSelector((state) => state.users.roles);

  const defaultValues = {
    first_name: "",
    last_name: "",
    emp_id: "",
    email: "",
    mobile: "",
    birth_date: "",
    role_id: null,
  };

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues,
    mode: "onTouched",
    resolver: zodResolver(createUserSchema),
  });

  const roleOptions = useMemo(() => {
    // state.users.roles can be either a bare array (non-paginated /roles) or
    // the paginated shape { rolesData, count } (used by the roles table). Handle
    // both so this modal never calls .map on a non-array.
    const list = Array.isArray(rolesData)
      ? rolesData
      : Array.isArray(rolesData?.rolesData)
      ? rolesData.rolesData
      : [];
    return list.map((r) => ({
      value: r.ID,
      label: r.role_key,
    }));
  }, [rolesData]);

  /***UPDATE COMPONENT */
  useEffect(() => {
    if (TYPE === "UPDATE" && selectedUser) {
      reset({
        first_name: selectedUser?.first_name,
        last_name: selectedUser?.last_name,
        emp_id: selectedUser?.emp_id,
        email: selectedUser?.email,
        mobile: selectedUser?.mobile,
        birth_date: moment(selectedUser.birth_date).format("YYYY-MM-DD"),
        role_id: selectedUser?.role_id,
      });
    }
  }, [selectedUser, TYPE]);

  /***Fetch user roles* */
  useEffect(() => {
    dispatch(getAllRoles());
  }, [dispatch]);

  console.log("selectedUser", selectedUser);

  const onSubmit = useCallback(
    async (data) => {
      if (!data.birth_date) {
        toast.error("Please select Date of birth");
        return;
      }
      console.log("data", data);

      const payload = {
        ...data,
        birth_date: new Date(data?.birth_date).toISOString(),
      };

      const DEFAULT_PASSWORD = defaultPasswordGenerate(data?.first_name);

      /**Creating user */
      if (TYPE === "CREATE") {
        payload["password"] = DEFAULT_PASSWORD;
      }

      if (TYPE === "UPDATE") {
        delete payload.password;
        payload.ID = selectedUser?.ID;
      }

      if (TYPE === "UPDATE" && !payload.ID) {
        toast.error("User ID is missing or invalid.");
        return;
      }

      if (TYPE === "CREATE") {
        try {
          const resultAction = await dispatch(createUser(payload));

          if (createUser.fulfilled.match(resultAction)) {
            toast.success("User created successfully!");
            reset({
              ...defaultValues,
            });
            toggle();

            if (fetchUser) {
              fetchUser();
            }
            // ✅ Success feedback
            Swal.fire({
              title: "User Created!",
              text: "The user has been created successfully.",
              icon: "success",
              confirmButtonColor: "#3085d6",
            });
          } else {
            // If rejectWithValue was used, the message will be in payload
            const msg =
              typeof resultAction.payload === "string"
                ? resultAction.payload
                : resultAction.payload?.message ||
                  resultAction.error?.message ||
                  "Failed to create user. Please try again.";

            toast.error(msg);
            console.error("Create user failed:", resultAction);
          }
        } catch (err) {
          console.error("Unexpected error creating user:", err);
          toast.error("Unexpected error occurred while creating user.");
        }
      }

      if (TYPE === "UPDATE") {
        try {
          const resultAction = await dispatch(
            updateUser({ id: selectedUser?.ID, payload, token }),
          );

          if (updateUser.fulfilled.match(resultAction)) {
            toast.success("User update successfully!");
            reset({
              ...defaultValues,
            });
            toggle();

            if (fetchUser) {
              fetchUser();
            }

            // ✅ Success feedback
            Swal.fire({
              title: "User Updated!",
              text: "The user has been updated successfully.",
              icon: "success",
              confirmButtonColor: "#3085d6",
            });
          } else {
            // If rejectWithValue was used, the message will be in payload
            const msg =
              typeof resultAction.payload === "string"
                ? resultAction.payload
                : resultAction.payload?.message ||
                  resultAction.error?.message ||
                  "Failed to create user. Please try again.";

            toast.error(msg);
            console.error("Create user failed:", resultAction);
          }
        } catch (err) {
          console.error("Unexpected error creating user:", err);
          toast.error("Unexpected error occurred while creating user.");
        }
      }
    },
    [dispatch, reset, toggle],
  );

  const maxDate = useMemo(() => new Date().toISOString().split("T")[0], []);

  console.log("Errors", errors);
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static">
      <ModalHeader>
        {TYPE === "UPDATE" ? "Update User" : "Create User"}
      </ModalHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <div className="form-row">
            <FormGroup className="col-md-12">
              <Row>
                <Col sm="6">
                  <Label>
                    First Name<span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="first_name"
                    control={control}
                    rules={{
                      required: "First name is required",
                      maxLength: { value: 100, message: "Max 100 chars" },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        placeholder="Enter first name"
                        invalid={!!errors.first_name}
                        disabled={isSubmitting}
                        required
                      />
                    )}
                  />
                  {errors.first_name && (
                    <small className="text-danger">
                      {errors.first_name.message}
                    </small>
                  )}
                </Col>

                <Col sm="6">
                  <Label>Last Name<span className="text-danger">*</span></Label>
                  <Controller
                    name="last_name"
                    control={control}
                    rules={{
                      required: "Last name is required",
                      maxLength: { value: 100, message: "Max 100 chars" },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        placeholder="Enter last name"
                        invalid={!!errors.last_name}
                        disabled={isSubmitting}
                        required
                      />
                    )}
                  />
                  {errors.last_name && (
                    <small className="text-danger">
                      {errors.last_name.message}
                    </small>
                  )}
                </Col>
              </Row>
            </FormGroup>

            <FormGroup className="col-md-12">
              <Row>
                <Col sm="6">
                  <Label>Employee ID<span className="text-danger">*</span></Label>
                  <Controller
                    name="emp_id"
                    control={control}
                    rules={{
                      required: "Employee Id is required",
                      maxLength: { value: 100, message: "Max 100 chars" },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        placeholder="Enter employee Id"
                        invalid={!!errors.emp_id}
                        disabled={isSubmitting}
                        required
                      />
                    )}
                  />
                  {errors.emp_id && (
                    <small className="text-danger">
                      {errors?.emp_id?.message}
                    </small>
                  )}
                </Col>

                <Col sm="6">
                  <Label>Mobile Number<span className="text-danger">*</span></Label>
                  <Controller
                    name="mobile"
                    control={control}
                    rules={{
                      required: "Mobile number is required",
                      pattern: {
                        value: /^[0-9]{7,15}$/,
                        message: "Enter valid mobile number",
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Enter mobile No."
                        type="text"
                        invalid={!!errors.mobile}
                        disabled={isSubmitting}
                        required
                      />
                    )}
                  />
                  {errors.mobile && (
                    <small className="text-danger">
                      {errors.mobile.message}
                    </small>
                  )}
                </Col>
              </Row>
            </FormGroup>

            <FormGroup className="col-md-12">
              <Row>
                <Col sm="6">
                  <Label>Email ID<span className="text-danger">*</span></Label>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value:
                          /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                        message: "Enter valid email",
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Enter email Id"
                        type="email"
                        invalid={!!errors.email}
                        disabled={isSubmitting}
                        required
                      />
                    )}
                  />
                  {errors.email && (
                    <small className="text-danger">
                      {errors.email.message}
                    </small>
                  )}
                </Col>

                <Col sm="6">
                  <FormGroup>
                    <Label className="form-label">Date of Birth<span className="text-danger">*</span></Label>
                    <Controller
                      name="birth_date"
                      control={control}
                      rules={{ required: "Date of birth is required" }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="date"
                          invalid={!!errors.birth_date}
                          max={maxDate}
                          disabled={isSubmitting}
                          required
                        />
                      )}
                    />
                    {errors.birth_date && (
                      <small className="text-danger">
                        {errors.birth_date.message}
                      </small>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            </FormGroup>

            <FormGroup className="col-md-12">
              <Row>
                <Col sm="12">
                  <Label>Role<span className="text-danger">*</span></Label>
                  <Controller
                    name="role_id"
                    control={control}
                    rules={{ required: "Role is required" }}
                    render={({ field, fieldState }) => {
                      const selected =
                        roleOptions.find((o) => o.value === field.value) ||
                        null;
                      return (
                        <>
                          <Select
                            options={roleOptions}
                            value={selected}
                            onChange={(opt) =>
                              field.onChange(opt ? opt.value : null)
                            }
                            required
                            className="js-example-basic-single col-sm-12 min"
                            isDisabled={isSubmitting}
                            placeholder="Select role"
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                outlineColor: fieldState.error
                                  ? "red"
                                  : base.outlineColor,
                                boxShadow: fieldState.error
                                  ? "0 0 0 0.5px red"
                                  : state.isFocused
                                    ? base.boxShadow
                                    : "none",
                                "&:hover": {
                                  outlineColor: fieldState.error
                                    ? "red"
                                    : base.outlineColor,
                                },
                              }),
                            }}
                          />
                          {fieldState.error && (
                            <small className="text-danger">
                              {fieldState.error.message}
                            </small>
                          )}
                        </>
                      );
                    }}
                  />
                </Col>
              </Row>
            </FormGroup>
          </div>
        </ModalBody>

        <ModalFooter className="d-flex customModalFooter">
          <button
            type="submit"
            className="btn bg-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? <Spinner size={"sm"} /> : null}
            <span style={{ paddingLeft: "5px" }}>
              {isSubmitting
                ? "Submitting"
                : TYPE === "UPDATE"
                  ? "Update"
                  : "Create"}
            </span>
          </button>
          <button
            type="button"
            className="btn bg-danger"
            onClick={() => {
              if (TYPE === "CREATE") {
                reset(defaultValues);
              }
              toggle();
            }}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CreateUserModal;
